import { FC, memo, useEffect, useRef } from 'react';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { notification } from 'antd';
import { currentProjectAtom } from '~/stores/ProjectStore';
import {
  activeUploadTasksCountAtom,
  uploadTaskListRefreshTrigger,
  uploadTasksAtom,
} from '~/stores/UploadTaskStore';
import { mapDataListAtom, mapDataListRefreshTrigger } from '../stores/mapStore';
import { UploadTaskAPIs } from '~/common/typescript/apis/UploadTaskAPIs';
import { UploadTask_Status } from '~/common/typescript/models/UploadTask';
import { remote_mapData_getByProject } from '~/common/typescript/apis/MapDataAPIs';
import { MapData_ProcessStatus } from '~/common/typescript/models/MapData';

/**
 * Controller to monitor and poll upload task and map data processing progress in the background.
 * Updates activeUploadTasksCountAtom and automatically notifies when processing finishes.
 */
const UploadTaskProgressController: FC = () => {
  const currentProject = useAtomValue(currentProjectAtom);
  const [activeCount, setActiveCount] = useAtom(activeUploadTasksCountAtom);
  const setUploadTasks = useSetAtom(uploadTasksAtom);
  const setMapDataList = useSetAtom(mapDataListAtom);
  const setMapDataRefreshTrigger = useSetAtom(mapDataListRefreshTrigger);
  const refreshTrigger = useAtomValue(uploadTaskListRefreshTrigger);

  const prevActiveCountRef = useRef<number>(0);
  const isCheckingRef = useRef<boolean>(false);

  const checkStatus = async () => {
    if (isCheckingRef.current) return;
    isCheckingRef.current = true;

    try {
      let activeTasks = 0;
      let activeMapDatas = 0;

      // 1. Fetch recent upload tasks
      const taskParams: any = {
        page: 1,
        pageSize: 10,
      };
      if (currentProject?.id) {
        taskParams.project_id = currentProject.id;
      }

      const taskRes = await UploadTaskAPIs.remote_getPageUploadTasks(taskParams);
      if (taskRes && taskRes.results) {
        setUploadTasks(taskRes.results);
        activeTasks = taskRes.results.filter(
          (t) => t.status === UploadTask_Status.PENDING || t.status === UploadTask_Status.PROCESSING,
        ).length;
      }

      // 2. Fetch project map data to verify tiling progress
      if (currentProject?.id) {
        const mapDatas = await remote_mapData_getByProject(currentProject.id);
        if (mapDatas && Array.isArray(mapDatas)) {
          activeMapDatas = mapDatas.filter((m) =>
            [
              MapData_ProcessStatus.SUBMITED,
              MapData_ProcessStatus.PROCESSING,
              MapData_ProcessStatus.PENDING,
            ].includes(m.processStatus),
          ).length;

          // If there were active tasks that just finished, update mapDataList
          if (prevActiveCountRef.current > 0 && activeTasks + activeMapDatas === 0) {
            setMapDataList((prev) => {
              return (mapDatas || []).filter(Boolean).map((newLayer: any) => {
                const existing = prev.find((l) => l?.id === newLayer?.id);
                return {
                  ...newLayer,
                  visible: existing?.visible ?? false,
                };
              });
            });
            setMapDataRefreshTrigger((prev) => prev + 1);
            notification.success({
              message: 'Tạo lát gạch hoàn tất!',
              description: 'Đã hoàn tất xử lý tạo lát gạch bản đồ. Các lớp dữ liệu đã sẵn sàng hiển thị.',
              placement: 'bottomRight',
              duration: 4.5,
            });
          }
        }
      }

      const totalActive = activeTasks + activeMapDatas;
      prevActiveCountRef.current = totalActive;
      setActiveCount(totalActive);
    } catch (err) {
      console.error('Error checking upload task status:', err);
    } finally {
      isCheckingRef.current = false;
    }
  };

  // Immediate check on project change or manual refresh trigger
  useEffect(() => {
    checkStatus();
  }, [currentProject?.id, refreshTrigger]);

  // Polling effect: poll every 3 seconds while active tasks exist
  useEffect(() => {
    let intervalId: any = null;

    if (activeCount > 0) {
      intervalId = setInterval(() => {
        checkStatus();
      }, 3000);
    }

    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [activeCount, currentProject?.id]);

  return null;
};

export default memo(UploadTaskProgressController);
